"use client";

import {
  getUserLastActiveAt,
  updateUserLastActiveAt,
} from "@punchup/data/legacy";
import { useCallback, useEffect, useState } from "react";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import useSession from "./useSession";

/**
 * This hook returns the timestamp a user was last active at on the site.
 * Under the hood: if a user doesn't have a timestamp yet, it stores one,
 * and the current timestamp is updated only every 2 hours at most to avoid
 * unnecessary calls.
 */
export default function useLastActiveAt() {
  const [lastActive, setLastActive] = useState<string | null>(null);
  const session = useSession();
  const supabase = createClientComponentClient<Database>();

  const fetchLastActiveAt = useCallback(async () => {
    // This snippet takes the current time, converts it into UTC and then stores it into a string
    // This way we'll always store times in UTC in the DB, but they will be "accurate" as in user timezone
    // is included in the calculation.
    const currentTime = dayjs.utc().toISOString();
    const lastActiveAt = await getUserLastActiveAt(supabase);
    // Since we use this hook to calculate notifications, we're only interested in returning the last previously time active, not the current time, if being updated
    setLastActive(lastActiveAt);
    if (lastActiveAt === null) {
      await updateUserLastActiveAt(supabase, currentTime);
      // Override: if a user doesn't have a lastActive in the DB, in addition to updating the time (line above)
      // we want to return the current datetime minus two weeks - so that the user will see some notifications
      // instead of nothing.
      setLastActive(dayjs(currentTime).subtract(2, "week").toISOString());
    } else {
      // By default dayjs parses and displays in local time, so in this case both the `dayjs()` and the `dayjs(lastActiveAt)` will figure
      // as local time (local to the user), so no need to use .utc here.
      const timeDifference = dayjs().diff(dayjs(lastActiveAt), "minutes");
      //   Only update last_active_at if the difference from the last update is greater than 120 minutes (2 hours)
      if (timeDifference > 120) {
        await updateUserLastActiveAt(supabase, currentTime);
      }
    }
  }, [supabase]);

  useEffect(() => {
    if (session?.user) {
      fetchLastActiveAt();
    }
  }, [fetchLastActiveAt, session?.user]);

  return lastActive;
}
